/* eslint-disable multiline-ternary */
import React, { useEffect, useState } from 'react'
import { useEvent } from '@emerald-works/react-event-bus-client'
import { useFormFactor, usePage } from '../../hooks'
import { campaignSlice, searchResultsSlice } from '../../reducers'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate, useLocation } from 'react-router'
import {
  Container,
  PageContainer,
  List,
  Pagination,
  AddToPlaylist
} from '../../components'
import { contentUrl } from '../../helpers/url'
import {
  Header,
  FilterBar,
  SearchSuggestions,
  SearchMetaTags
} from '../../components/search-results'
import { useEventFilters, useSearchQuery } from './hooks'
import { sorters } from './params'
import { ContentCard } from '../../components/general-card'
import WithLoading from '../../components/with-loading'
import { useAddToPlaylist } from '../../components/add-to-playlist-dialog'
import { useStyles } from './style'
import { useAddToBookmarks } from '../../components/add-to-bookmarks'
import { ALLOW_PLAYLISTS_GROUP } from '../../components/user-group-policy/permissions'
import { UserGroupPolicy } from '../../components/user-group-policy'
import { Box, Grid } from '@mui/material'
import useCampaignList from '../../hooks/useCampaignList'
import { VerticalCampaignBanner } from '../../components/campaign/vertical-banner'
import { DesktopCampaignBanner } from '../../components/campaign/home-banner'
import useContentSearchedEvent from './hooks/useContentSearchedEvent'
import { ToolkitPrivateRoute } from './../../components/toolkit-private-route'

const SearchResultsPage = () => {
  const classes = useStyles()
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const { getEventFilters } = useEventFilters()

  const { params, filters, clearFilters, addFilter } = useSearchQuery()

  const results = useSelector(searchResultsSlice.selectors.selectResults)
  const total = useSelector(searchResultsSlice.selectors.selectTotal)
  const counts = useSelector(searchResultsSlice.selectors.selectCounts)

  const campaign = useSelector(campaignSlice.selectors.campaignData)

  const {
    pageStart,
    pageSize,
    pageCurrent,
    pageCount,
    calcPageStart
  } = usePage(params, total)

  const { isBookmarked, handleBookmark, bookmarking } = useAddToBookmarks(
    results
  )
  const { showAddToPlaylist, getPlaylistArticle } = useAddToPlaylist()

  const { hideLeftBanner, listColumns } = useCampaignList()
  const { isMobile, isLargeTablet } = useFormFactor()

  const [getSearchResults] = useEvent([
    searchResultsSlice.eventBus.getSearchResults
  ])

  useContentSearchedEvent({
    event: getSearchResults,
    filters,
    from: location.state?.from ?? 'search-results',
    position: location.state?.position
  })

  const [searchTerm, setSearchTerm] = useState()

  useEffect(() => () => dispatch(searchResultsSlice.actions.reset()), [dispatch])

  // Only after a search.
  useEffect(() => {
    if (!getSearchResults.isWorking) {
      setSearchTerm(filters.search)
    }
  }, [getSearchResults.isWorking]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!filters.search || pageStart <= 0 || !pageSize) return
    getSearchResults.trigger({
      filters: getEventFilters(filters),
      size: pageSize,
      from: pageStart - 1
    })
  }, [getSearchResults, filters, pageStart, pageSize, getEventFilters])

  useEffect(() => {
    if (!params.search) {
      dispatch(searchResultsSlice.actions.reset())
    }
  }, [params.search, dispatch])

  useEffect(() => {
    if (!getSearchResults.isWorking) {
      window.scrollTo(0, 0)
    }
  }, [getSearchResults.isWorking])

  const isSearching =
    getSearchResults.isWorking ||
    (params.search && params.search !== searchTerm)

  const handlePageChange = (evt, newPage) => {
    navigate(addFilter({ start: calcPageStart(newPage) }))
  }

  const handleCardSelect = item => navigate(contentUrl(item, 'search'))

  const handleFilterApply = ({ name, value }) =>
    navigate(addFilter({ [name]: value }))

  const handleFilterClearAll = () => navigate(clearFilters())

  return (
    <PageContainer>
      <SearchMetaTags searchTerm={filters.search} />
      <UserGroupPolicy
        accessGroup={ALLOW_PLAYLISTS_GROUP}
        componentAllow={
          <AddToPlaylist
            article={getPlaylistArticle()}
            backText='Back to Category'
          />
        }
      />
      <Container maxContentWidth={1200} inset={3} className={classes.root}>
        <WithLoading loading={isSearching}>
          <Header
            term={filters.search}
            total={total}
            className={classes.header}
          />
        </WithLoading>
        {isSearching || total > 0 ? (
          <Grid container>
            {!hideLeftBanner && (
              <Grid item xs={4} sm={isLargeTablet ? 3 : 4} lg={3}>
                <VerticalCampaignBanner campaign={campaign} />
              </Grid>
            )}
            <Grid item xs>
              <FilterBar
                disabled={isSearching}
                counts={counts}
                sorters={sorters}
                values={filters}
                onApply={handleFilterApply}
                onClearAll={handleFilterClearAll}
                classes={{ root: classes.filterBar }}
              />
              <List
                results={isSearching ? Array(pageSize).fill({}) : results}
                columns={listColumns}
                renderItem={item => (
                  <ContentCard
                    content={!isSearching && item}
                    bookmarked={isBookmarked(item)}
                    classes={{ root: classes.card }}
                    actionLoading={bookmarking(item)}
                    onSelect={() => handleCardSelect(item)}
                    onBookmark={() => handleBookmark(item)}
                    onAddToPlaylist={showAddToPlaylist}
                  />
                )}
              />
            </Grid>
            <Pagination
              disabled={isSearching}
              page={pageCurrent}
              count={pageCount}
              size={pageSize}
              onChange={handlePageChange}
              className={classes.pagination}
            />
            {hideLeftBanner && (
              <Box mt={3} display='flex' flex={1} justifyContent='center' alignItems='center'>
                {!isMobile ? <DesktopCampaignBanner campaign={campaign} /> : <VerticalCampaignBanner campaign={campaign} />}
              </Box>
            )}
          </Grid>
        ) : (
          <SearchSuggestions />
        )}
      </Container>
    </PageContainer>
  )
}

const Page = () => {
  return (
    <ToolkitPrivateRoute
      featureGuard='contentHub'
      publicOnlyForPublicTenant
      element={<SearchResultsPage />}
    />
  )
}

export {
  Page as Component
}
